import * as stylex from "@stylexjs/stylex";
import { sum } from "d3-array";
import { replicate } from "fp-ts/lib/Array";
import React from "react";
import { useContentRect, useRTL } from "../hooks";
import { breakpoints, colors } from "../materials/consts.stylex";
import { typography } from "../materials/typography.stylex";
import { An, Ar } from "../prelude";
const COLUMN_PADDING = 20;
const styles = {
    title: {
        kDwRjp: "x6ii1vp",
        $$css: true
    }
};
/**
 * ChartGrid
 *
 * Lays out children in a grid where each cell can not shrink below the given
 * min width.
 *
 * In the following example, a desired column count of 2 has been specified, but
 * three cells were given.
 *
 * +-- Row 1 ------------+
 * | +--------+--------+ |
 * | | Cell 1 | Cell 2 | |
 * | +--------+--------+ |
 * +---------------------+
 * +-- Row 2 ------------+
 * | +--------+          |
 * | | Cell 3 |          |
 * | +--------+          |
 * +---------------------+
 */ export function ChartGrid({ children, data, getCell, minWidth = 140, columnCount = 1, columnFractions }) {
    const isRTL = useRTL();
    const [ref, contentRect] = useContentRect();
    const isReady = contentRect.width > 0;
    const fullWidth = contentRect.width > 0 ? contentRect.width : minWidth;
    const possibleColumns = Math.floor(fullWidth / (minWidth + COLUMN_PADDING));
    const cols = possibleColumns >= columnCount ? columnCount : Math.max(possibleColumns, 1);
    const columnWidth = Math.floor((fullWidth - COLUMN_PADDING * (cols - 1)) / cols) - 1;
    const getCellWidth = React.useMemo(()=>{
        const fractions = columnFractions ? columnFractions.length === cols ? columnFractions : columnFractions.concat(replicate(cols, 1)).slice(0, cols) : replicate(cols, 1);
        const fr = sum(fractions) / fractions.length;
        return (stdWidth, idx)=>stdWidth / fr * (fractions[idx] || 1);
    }, [
        cols,
        columnFractions
    ]);
    const cells = An.groupBy(getCell)(data);
    const rows = Ar.chunksOf(cols)(Object.keys(cells));
    return <div ref={ref}>
      {isReady && rows.map((row, ir)=>{
        const last = ir === rows.length - 1;
        const rowProps = {
            0: {
                className: "xrvj5dj x5h996b xj564av x1fdkxpm xk3lak5 xcpzygv xzt5al7"
            },
            1: {
                className: "xrvj5dj x5h996b xj564av x1fdkxpm xk3lak5 xcpzygv xzyj77d"
            }
        }[!!isRTL << 0];
        return <div key={`row-${ir}`} {...rowProps} style={{
            ...rowProps.style,
            gridTemplateAreas: genGridTemplateArea(cols),
            gridTemplateColumns: Array.from({
                length: cols
            }, (_, i)=>`${getCellWidth(columnWidth, i)}px`).join(" ")
        }}>
              {row.map((cell, ic)=>{
            const i = ir * cols + ic;
            const xs = cells[cell];
            const first = i === 0;
            const firstInRow = i % cols === 0;
            const width = getCellWidth(columnWidth, ic);
            const colName = colNameTemplate[i % cols];
            let j = 0;
            const gridArea = ()=>({
                    style: {
                        gridArea: `${colName}${j++}`
                    }
                });
            const titleProps = stylex.props(typography.table, styles.title);
            return <React.Fragment key={cell}>
                    <h2 {...titleProps} style={{
                ...titleProps.style,
                ...gridArea().style
            }}>
                      {cell}
                    </h2>
                    <div {...gridArea()}>
                      {children({
                data: xs,
                width,
                first,
                firstInRow,
                last,
                cell,
                index: ic
            })}
                    </div>
                  </React.Fragment>;
        })}
            </div>;
    })}
    </div>;
}
// -----------------------------------------------------------------------------
/**
 * colNameTemplate
 *
 * We support a maximum of 10 columns; only the necessary columns are picked
 */ const colNameTemplate = [
    "a",
    "b",
    "c",
    "d",
    "e",
    "f",
    "g",
    "h",
    "i",
    "j"
];
/**
 * genGridTemplateArea
 *
 * A helper function to generate named and enumerated grid template areas. The
 * maximum row count is 10.
 *
 * Example:
 * grid-template-areas: "a0 b0" "a1 b1" "a3 b3" ... "a9 b9";
 */ function genGridTemplateArea(colCount) {
    const colNames = colNameTemplate.slice(0, colCount);
    return Array.from({
        length: 10
    }, (_, i)=>colNames.map((col)=>`${col}${i}`).join(" ")).map((row)=>`"${row}"`).join(" ");
}
